import React, { useState } from "react";
import {
  ArrowLeft,
  Sun,
  Moon,
  FileText,
  Shield,
  Users,
  AlertCircle,
  ExternalLink,
  Download,
  Globe,
} from "lucide-react";

const TermsAndConditions = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"
      }`}
    >
      {/* Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 border-b ${
          isDarkMode
            ? "bg-gray-900/95 backdrop-blur-md border-gray-800"
            : "bg-white/95 backdrop-blur-md border-gray-200"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => window.history.back()}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-100"
                }`}
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div className="flex items-center space-x-3">
                <FileText className="w-8 h-8 text-[#FEF200]" />
                <h1 className="text-xl font-semibold">Legal Terms</h1>
              </div>
            </div>

            <button
              onClick={toggleTheme}
              className={`p-2 rounded-full transition-colors ${
                isDarkMode
                  ? "bg-gray-800 text-[#FEF200] hover:bg-gray-700"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {isDarkMode ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div
            className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full mb-6 ${
              isDarkMode ? "bg-gray-800" : "bg-gray-100"
            }`}
          >
            <Shield className="w-5 h-5 text-[#FEF200]" />
            <span className="text-sm font-medium">
              Effective 3 days after notice
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl font-semibold mb-6">
            Terms & Conditions
          </h1>
          <p
            className={`text-lg leading-relaxed max-w-3xl mx-auto mb-6 ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Welcome to Homeegram - a digital networking & marketplace platform
            for the home construction & design ecosystem, connecting
            professionals, service providers, brands & clients.
          </p>

          <div
            className={`p-4 rounded-lg border-l-4 border-red-400 ${
              isDarkMode ? "bg-red-900/20" : "bg-red-50"
            }`}
          >
            <div className="flex items-start">
              <div>
                <p className="font-semibold mb-1 text-red-400">
                  Important Notice
                </p>
                <p className="text-sm">
                  IF YOU DO NOT AGREE TO THESE LEGAL TERMS, DO NOT USE THE
                  SERVICES.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Navigation */}
        <div
          className={`rounded-2xl p-6 mb-12 ${
            isDarkMode ? "bg-gray-800" : "bg-gray-50"
          }`}
        >
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-[#FEF200]" />
            Table of Contents
          </h3>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              "1. Definitions",
              "2. Acceptance, Changes & Notice Period",
              "3. Our Services & Marketplace Role",
              "4. User Representations & Registration",
              "5. Permissions & Why We Request Them",
              "6. Purchases, Payments & Subscriptions",
              "7. Refunds & Cancellations",
              "8. Account Deletion & Data Removal",
              "9. Privacy, Data Safety & Cookies",
              "10. Intellectual Property & Takedown",
              "11. User Content; Contributions Licence",
              "12. Prohibited Activities",
              "13. Mobile Application Licence",
              "14. Third-Party Websites, SDKs & Advertisers",
              "15. Services Management",
              "16. Limitation of Liability",
              "17. Indemnification",
              "18. Governing Law & Dispute Resolution",
              "19. Platform-Specific Terms",
              "20. Contact / Grievance Officer",
            ].map((item, index) => (
              <a
                key={index}
                href={`#section-${index + 1}`}
                className={`text-sm py-2 px-3 rounded-lg transition-colors ${
                  isDarkMode
                    ? "hover:bg-gray-700 text-gray-300 hover:text-white"
                    : "hover:bg-white text-gray-600 hover:text-gray-900"
                }`}
              >
                {item}
              </a>
            ))}
          </div>
        </div>

        {/* Terms Content */}
        <div className="space-y-12">
          {/* Section 1 - Definitions */}
          <section id="section-1" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                1
              </div>
              Definitions
            </h2>
            <div className="space-y-4">
              <div
                className={`p-4 rounded-lg ${
                  isDarkMode ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <ul className="space-y-3">
                  <li>
                    <strong>Account:</strong> Your registered account on the
                    Services.
                  </li>
                  <li>
                    <strong>App:</strong> The Homeegram mobile application
                    distributed through app stores.
                  </li>
                  <li>
                    <strong>Contributions / User Content:</strong> Content you
                    post to the Services (text, images, audio, video, reviews,
                    messages).
                  </li>
                  <li>
                    <strong>Grievance Officer:</strong> The person designated
                    under the IT Rules to receive complaints & takedown
                    notices.
                  </li>
                  <li>
                    <strong>Privacy Policy:</strong> Our Privacy Policy
                    available at https://homeegram.com/privacy-policy.
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 2 - Acceptance */}
          <section id="section-2" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                2
              </div>
              Acceptance, Changes & Notice Period
            </h2>
            <div className="space-y-4">
              <p>
                By using the Services you accept these Legal Terms. We may
                modify these Legal Terms from time to time. Changes will become
                effective three (3) days after notice is given, except changes
                necessary for security, bug fixes, court orders or minor
                functionality updates, which may be effective immediately.
              </p>
              <p>
                Continued use after changes confirms acceptance. If you
                disagree, stop using the Services &, where applicable,
                terminate your account.
              </p>
            </div>
          </section>

          {/* Section 3 - Services */}
          <section id="section-3" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                3
              </div>
              Our Services & Marketplace Role
            </h2>
            <div className="space-y-4">
              <p>
                Homeegram provides tools for discovery, professional profiles,
                project showcases, job postings, service listings, messaging,
                ratings & reviews, advertising, rewards (Homee Coin),
                verification badges, payments facilitation & related features.
              </p>

              <div
                className={`p-4 rounded-lg border-l-4 border-yellow-400 ${
                  isDarkMode ? "bg-yellow-900/20" : "bg-yellow-50"
                }`}
              >
                <h3 className="font-semibold mb-2 text-[#FEF200]">
                  Marketplace & Third-party Transactions (Important)
                </h3>
                <p className="text-sm mb-3">
                  Homeegram is a technology platform that connects users &
                  service providers. Homeegram is not the seller or service
                  provider in any contract between users & third parties. Any
                  contract for goods or services is directly between you & the
                  third-party provider.
                </p>
                <ul className="text-sm space-y-1 list-disc list-inside">
                  <li>
                    Homeegram does not guarantee, endorse, insure or assume
                    responsibility for the performance, quality, legality,
                    safety or compliance of third-party goods or services.
                  </li>
                  <li>
                    Payment processing for third-party transactions is handled
                    by authorised payment service providers; their terms apply.
                  </li>
                  <li>
                    Disputes between consumers & providers should be resolved
                    between those parties.
                  </li>
                </ul>
              </div>

              <p className="text-sm">
                <strong>Notes & limitations:</strong> Feature availability may
                vary by geography, account type & app version. Homeegram may
                enable, disable or change features at any time.
              </p>
            </div>
          </section>

          {/* Section 4 - User Registration */}
          <section id="section-4" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                4
              </div>
              User Representations & Registration
            </h2>
            <div className="space-y-4">
              <p>
                You represent & warrant you are 18+ & have authority to
                enter these terms. Provide accurate registration info & keep
                credentials confidential.
              </p>
              <p>
                We may require verification / KYC for certain features
                (payments, payouts, verification badges). We may suspend or
                terminate accounts for fraud, suspicious behaviour, or false
                information.
              </p>
            </div>
          </section>

          {/* Section 5 - Permissions */}
          <section id="section-5" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                5
              </div>
              Permissions & Why We Request Them
            </h2>
            <div className="space-y-4">
              <p>
                We request only permissions necessary for app features.
                Examples:
              </p>
              <div
                className={`p-4 rounded-lg ${
                  isDarkMode ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <ul className="space-y-2">
                  <li>
                    <strong>Camera:</strong> Capture project photos & profile
                    images.
                  </li>
                  <li>
                    <strong>Storage / Files & Media:</strong> Upload & cache
                    images & documents.
                  </li>
                  <li>
                    <strong>Location (approx):</strong> Show local listings &
                    nearby professionals (only with consent).
                  </li>
                  <li>
                    <strong>Contacts (optional):</strong> Invite colleagues
                    (only with explicit consent).
                  </li>
                  <li>
                    <strong>Microphone (optional):</strong> Record voice
                    messages where enabled.
                  </li>
                </ul>
              </div>
              <p>
                You can revoke permissions anytime via device settings;
                revocation may limit functionality. We explain each permission
                in the App Settings → Privacy screen.
              </p>
            </div>
          </section>

          {/* Section 6 - Payments */}
          <section id="section-6" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                6
              </div>
              Purchases, Payments & Subscriptions
            </h2>
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">In-app Purchases</h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-50"
                  }`}
                >
                  <h4 className="font-semibold mb-2 text-[#FEF200]">Android (Google Play)</h4>
                  <p className="text-sm">
                    Digital goods & subscriptions purchased in the Android App are
                    processed through Google Play Billing & are subject to Google
                    Play's terms, refund & subscription management policies. Use
                    Google Play subscription management to cancel auto-renewing
                    subscriptions.
                  </p>
                </div>

                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-50"
                  }`}
                >
                  <h4 className="font-semibold mb-2 text-[#FEF200]">iOS (App Store)</h4>
                  <p className="text-sm">
                    Digital goods & subscriptions purchased in the iOS App are
                    processed through the App Store & are subject to Apple's
                    terms, refund & subscription management policies. Use App
                    Store subscription management to cancel auto-renewing
                    subscriptions.
                  </p>
                </div>
              </div>

              <h3 className="text-lg font-semibold">
                Offline / Physical Services
              </h3>
              <p>
                For bookings or purchases of real-world goods/services, payments
                may be processed by third-party PSPs (card, UPI, PayPal). Those
                transactions are governed by the PSP's terms.
              </p>

              <p>
                <strong>Taxes:</strong> Applicable taxes (GST etc.) will be
                added to prices where required.
              </p>
            </div>
          </section>

          {/* Section 7 - Refunds */}
          <section id="section-7" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                7
              </div>
              Refunds & Cancellations
            </h2>
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-50"
                  }`}
                >
                  <h3 className="text-lg font-semibold mb-3">Google Play Purchases</h3>
                  <p className="text-sm">
                    Refunds are governed by Google Play policies; Google may deduct
                    refunded amounts from developer payouts. Homeegram will
                    cooperate with & supplement Google's refund process where
                    appropriate.
                  </p>
                </div>

                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-50"
                  }`}
                >
                  <h3 className="text-lg font-semibold mb-3">App Store Purchases</h3>
                  <p className="text-sm">
                    Refunds are governed by Apple's App Store policies & terms of service.
                    Apple handles refund requests directly through their support system.
                  </p>
                </div>
              </div>

              <h3 className="text-lg font-semibold">Outside App Stores</h3>
              <p>
                Refunds for off-platform payments follow our Refund Policy at
                https://homeegram.com/help & applicable law. We will comply
                with statutory consumer protections.
              </p>
            </div>
          </section>

          {/* Section 8 - Account Deletion */}
          <section id="section-8" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                8
              </div>
              Account Deletion & Data Removal
            </h2>
            <div className="space-y-4">
              <div
                className={`p-4 rounded-lg ${
                  isDarkMode ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <h3 className="font-semibold mb-2">In-app Deletion Process</h3>
                <p className="mb-3">
                  Delete your account in-app:{" "}
                  <strong>Settings → Account → Delete Account</strong>
                </p>
                <ul className="text-sm space-y-1 list-disc list-inside">
                  <li>
                    After confirmation we will immediately disable your account
                    from public view
                  </li>
                  <li>
                    Personal data will be permanently deleted within 30 days
                  </li>
                  <li>
                    Some data may be retained for legal/tax/financial compliance
                    or to prevent fraud
                  </li>
                  <li>Backup copies may persist up to 180 days</li>
                </ul>
                <p className="mt-3 text-sm">
                  For assistance contact:{" "}
                  <strong>grievance@homeegram.com</strong>
                </p>
              </div>
            </div>
          </section>

          {/* Section 9 - Privacy */}
          <section id="section-9" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                9
              </div>
              Privacy, Data Safety & Cookies
            </h2>
            <div className="space-y-4">
              <p>
                Our Privacy Policy is incorporated by reference:
                https://homeegram.com/privacy-policy. We have completed app store
                data safety forms; the disclosures there match our Privacy Policy.
              </p>
              <p>
                A brief privacy summary & cookie controls are available in-app
                under Settings → Privacy.
              </p>
            </div>
          </section>

          {/* Section 10 - IP */}
          <section id="section-10" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                10
              </div>
              Intellectual Property & Takedown
            </h2>
            <div className="space-y-4">
              <p>
                Homeegram owns or licenses the Service content & Marks. If you believe content infringes your
                IP, send a notice to our Grievance Officer with details (see Contact). We will respond per applicable
                law. If content is removed & you believe removal was mistaken, submit a counter-notice per the
                procedure available at https://homeegram.com/grievance.
              </p>
            </div>
          </section>

          {/* Section 11 - User Content */}
          <section id="section-11" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                11
              </div>
              User Content; Contributions Licence
            </h2>
            <div className="space-y-4">
              <p>
                You own your Contributions. By posting, you grant Homeegram a perpetual (to the extent permitted
                by law), non-exclusive, transferable, sublicensable, royalty-free, worldwide licence to use &
                display your Contributions in connection with the Services. You represent you own or have rights to
                post such content.
              </p>
            </div>
          </section>

          {/* Section 12 - Prohibited Activities */}
          <section id="section-12" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                12
              </div>
              Prohibited Activities
            </h2>
            <div className="space-y-4">
              <div
                className={`p-4 rounded-lg border-l-4 border-red-400 ${
                  isDarkMode ? "bg-red-900/20" : "bg-red-50"
                }`}
              >
                <p className="mb-3">
                  Do not engage in the following activities:
                </p>
                <ul className="text-sm space-y-1 list-disc list-inside">
                  <li>
                    Post illegal, infringing, defamatory, pornographic, hateful,
                    harassing or violent content
                  </li>
                  <li>Impersonate others, scrape data, or send spam</li>
                  <li>Distribute malware or circumvent security measures</li>
                  <li>Use the Services for prohibited items/services</li>
                </ul>
                <p className="mt-3 text-sm font-semibold">
                  We may remove violating content & suspend/terminate
                  accounts.
                </p>
              </div>
            </div>
          </section>

          {/* Section 13 - Mobile Licence */}
          <section id="section-13" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                13
              </div>
              Mobile Application Licence
            </h2>
            <div className="space-y-4">
              <p>
                We grant a revocable, non-exclusive, non-transferable licence to install & use the App on devices
                you control, subject to these terms & the App Store's terms & conditions. Distributor policies
                (warranty, refunds) apply where relevant.
              </p>
            </div>
          </section>

          {/* Section 14 - Third Party Services */}
          <section id="section-14" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                14
              </div>
              Third-Party Websites, SDKs & Advertisers
            </h2>
            <div className="space-y-4">
              <p>
                We integrate with third-party websites, services & SDKs.
                Third-party sites & services have their own terms & privacy
                policies. SDKs & services used include:
              </p>

              <div className="grid md:grid-cols-2 gap-4">
                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-50"
                  }`}
                >
                  <h4 className="font-semibold mb-2 text-[#FEF200]">
                    Analytics & Performance
                  </h4>
                  <ul className="text-sm space-y-1">
                    <li>• Firebase Analytics & Crashlytics</li>
                    <li>• Sentry SDK - Error monitoring</li>
                  </ul>
                </div>

                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-50"
                  }`}
                >
                  <h4 className="font-semibold mb-2 text-[#FEF200]">
                    Advertising & Marketing
                  </h4>
                  <ul className="text-sm space-y-1">
                    <li>• Google AdMob - In-app advertising</li>
                    <li>• Facebook (Meta) SDK</li>
                    <li>• AppsFlyer - Attribution analytics</li>
                  </ul>
                </div>

                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-50"
                  }`}
                >
                  <h4 className="font-semibold mb-2 text-[#FEF200]">
                    Payment & Financial
                  </h4>
                  <ul className="text-sm space-y-1">
                    <li>• Razorpay SDK - Payment processing</li>
                    <li>• Google Play Billing</li>
                    <li>• App Store - In-app purchases</li>
                  </ul>
                </div>

                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-50"
                  }`}
                >
                  <h4 className="font-semibold mb-2 text-[#FEF200]">
                    Infrastructure & Other
                  </h4>
                  <ul className="text-sm space-y-1">
                    <li>• Ola Maps API - Location services</li>
                    <li>• Amazon Web Services (AWS)</li>
                    <li>• Bunny CDN - Content delivery</li>
                    <li>• Meile Search - Search functionality</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section 15 - Services Management */}
          <section id="section-15" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                15
              </div>
              Services Management
            </h2>
            <div className="space-y-4">
              <p>
                We may modify or suspend Services for maintenance or legal compliance. We will use reasonable
                efforts to notify users of scheduled maintenance; we do not guarantee uninterrupted access.
              </p>
            </div>
          </section>

          {/* Section 16 - Liability */}
          <section id="section-16" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                16
              </div>
              Limitation of Liability
            </h2>
            <div className="space-y-4">
              <div
                className={`p-4 rounded-lg border-l-4 border-blue-400 ${
                  isDarkMode ? "bg-blue-900/20" : "bg-blue-50"
                }`}
              >
                <p className="mb-2">
                  To the extent permitted by law, Homeegram's aggregate
                  liability will not exceed the total amount paid by you in the
                  12 months preceding the claim (or INR 10,000 (₹10,000) if you
                  never paid).
                </p>
                <p className="text-sm">
                  Nothing limits liability for gross negligence, willful
                  misconduct, fraud or non-excludable statutory liabilities.
                </p>
              </div>
            </div>
          </section>

          {/* Section 17 - Indemnification */}
          <section id="section-17" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                17
              </div>
              Indemnification
            </h2>
            <div className="space-y-4">
              <p>
                You agree to indemnify Homeegram from claims arising from your Contributions, breach of terms, or
                violation of third-party rights.
              </p>
            </div>
          </section>

          {/* Section 18 - Governing Law */}
          <section id="section-18" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                18
              </div>
              Governing Law & Dispute Resolution
            </h2>
            <div className="space-y-4">
              <p>
                These Legal Terms are governed by Indian law. Subject to
                mandatory consumer protections, disputes shall be subject to the
                exclusive jurisdiction of the courts in Ernakulam, Kerala.
              </p>
              <p>Parties are encouraged to attempt mediation first.</p>
            </div>
          </section>

          {/* Section 19 - Platform-Specific Terms */}
          <section id="section-19" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                19
              </div>
              Platform-Specific Terms
            </h2>
            <div className="space-y-6">
              <div
                className={`p-6 rounded-lg ${
                  isDarkMode ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <div className="w-6 h-6 bg-blue-500 rounded mr-2"></div>
                  iOS App Store Terms
                </h3>
                <p className="mb-3">
                  iOS users are additionally bound by Apple's App Store Review Guidelines, 
                  terms of service, & App Store Connect Developer Terms.
                </p>
                <p className="text-sm">
                  In case of conflict between these terms & Apple's terms, Apple's 
                  terms shall prevail for iOS-specific functionality.
                </p>
              </div>

              <div
                className={`p-6 rounded-lg ${
                  isDarkMode ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <div className="w-6 h-6 bg-green-500 rounded mr-2"></div>
                  Google Play Store Terms
                </h3>
                <p className="mb-3">
                  Android users are additionally bound by Google Play's Developer 
                  Policy & terms of service.
                </p>
                <p className="text-sm">
                  Google Play's policies & billing terms apply to all Android 
                  in-app purchases & subscriptions.
                </p>
              </div>
            </div>
          </section>

          {/* Section 20 - Contact */}
          <section id="section-20" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                20
              </div>
              Contact / Grievance Officer
            </h2>
            <div className="space-y-4">
              <div
                className={`p-6 rounded-lg ${
                  isDarkMode ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <h3 className="font-semibold mb-4">Grievance Officer</h3>
                <div className="space-y-2">
                  <p>
                    <strong>Name:</strong> Abijith Biju K
                  </p>
                  <p>
                    <strong>Email:</strong> grievance@homeegram.com
                  </p>
                  <p>
                    <strong>Support:</strong> abijith@homeeera.com
                  </p>
                  <p>
                    <strong>Phone:</strong> +91 95395 61999
                  </p>
                  <div className="mt-4">
                    <p>
                      <strong>Address:</strong>
                    </p>
                    <p className="text-sm">
                      NH 544, First Floor, G.I Square
                      <br />
                      Opposite Arya Bhangy Motors
                      <br />
                      Kothakulangara, Angamaly
                      <br />
                      Ernakulam, Kerala 683572, India
                    </p>
                  </div>
                </div>
              </div>

              <div
                className={`p-4 rounded-lg border-l-4 border-green-400 ${
                  isDarkMode ? "bg-green-900/20" : "bg-green-50"
                }`}
              >
                <h4 className="font-semibold mb-2 text-green-400">
                  Complaints Timeline (IT Rules)
                </h4>
                <ul className="text-sm space-y-1">
                  <li>• We will acknowledge complaints within 24 hours</li>
                  <li>• We endeavour to resolve them within 15 days</li>
                  <li>
                    • Complaints involving non-consensual sexually explicit
                    material will be prioritised & resolved within 72 hours
                    where applicable
                  </li>
                </ul>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div
          className={`mt-16 p-8 rounded-2xl text-center ${
            isDarkMode
              ? "bg-gradient-to-r from-gray-800 to-gray-700"
              : "bg-gradient-to-r from-gray-50 to-gray-100"
          }`}
        >
          <h3 className="text-xl font-semibold mb-4">
            Questions about these Terms?
          </h3>
          <p
            className={`mb-6 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
          >
            Contact our support team or grievance officer for any
            clarifications.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:abijith@homeeera.com"
              className="bg-[#FEF200] hover:bg-yellow-500 text-black font-semibold py-3 px-8 rounded-full transition-colors inline-flex items-center justify-center"
            >
              Contact Support
            </a>
            <a
              href="mailto:grievance@homeegram.com"
              className={`border-2 font-semibold py-3 px-8 rounded-full transition-colors inline-flex items-center justify-center ${
                isDarkMode
                  ? "border-gray-600 text-white hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              Grievance Officer
            </a>
          </div>
        </div>
      </main>

      <style jsx>{`
        html {
          scroll-behavior: smooth;
        }

        .prose {
          line-height: 1.6;
        }

        .prose p {
          margin-bottom: 1rem;
        }

        .prose-invert {
          color: #e5e7eb;
        }
      `}</style>
    </div>
  );
};

export default TermsAndConditions;